<template>
  <PageLayout :title="$t('admin.plans_title')" :subtitle="$t('admin.plans_subtitle')">
    <template #actions>
      <button class="btn btn-primary btn-sm" @click="openCreate">
        <i class="fas fa-plus mr-1"></i> {{ $t('admin.add_plan') }}
      </button>
    </template>

    <ContentCard>
      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('global.slug') }}</th>
              <th>{{ $t('global.name') }}</th>
              <th>{{ $t('global.price') }}</th>
              <th>IDR</th>
              <th>{{ $t('global.interval') }}</th>
              <th>{{ $t('global.active') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="plan in plans" :key="plan.id">
              <td><code>{{ plan.slug }}</code></td>
              <td>{{ plan.name }}</td>
              <td>{{ formatUSD(plan.price) }}</td>
              <td>{{ formatIDR(plan.price_idr) }}</td>
              <td>{{ plan.interval }}</td>
              <td><span class="badge" :class="plan.is_active ? 'badge-success' : 'badge-secondary'">{{ plan.is_active ? $t('global.yes') : $t('global.no') }}</span></td>
              <td>
                <button class="btn btn-xs btn-outline-primary mr-1" @click="openEdit(plan)">{{ $t('common.edit') }}</button>
                <button
                  v-if="plan.slug !== 'free' && plan.is_active"
                  class="btn btn-xs btn-outline-danger"
                  @click="onDeactivate(plan)"
                >
                  {{ $t('admin.deactivate') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <modal name="plan-modal" height="auto" :adaptive="true" width="560" :scrollable="true">
      <div class="modal-pad" v-if="form">
        <h5 class="mb-3">{{ form.id ? $t('admin.edit_plan') : $t('admin.add_plan') }}</h5>
        <div class="row">
          <div class="col-md-6 form-group">
            <label>{{ $t('global.slug') }}</label>
            <input v-model="form.slug" class="form-control" :disabled="form.id && form.slug === 'free'" />
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('global.name') }}</label>
            <input v-model="form.name" class="form-control" />
          </div>
          <div class="col-12 form-group">
            <label>{{ $t('admin.description') }}</label>
            <textarea v-model="form.description" class="form-control" rows="2"></textarea>
          </div>
          <div class="col-md-4 form-group">
            <label>{{ $t('admin.price_usd') }}</label>
            <input v-model.number="form.price" type="number" step="0.01" class="form-control" />
          </div>
          <div class="col-md-4 form-group">
            <label>{{ $t('admin.price_idr') }}</label>
            <input v-model.number="form.price_idr" type="number" class="form-control" />
          </div>
          <div class="col-md-4 form-group">
            <label>{{ $t('global.interval') }}</label>
            <select v-model="form.interval" class="form-control">
              <option v-for="opt in intervalOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.paypal_plan_id') }}</label>
            <input v-model="form.paypal_plan_id" class="form-control" />
          </div>
          <div class="col-md-3 form-group">
            <label>{{ $t('admin.sort_order') }}</label>
            <input v-model.number="form.sort_order" type="number" class="form-control" />
          </div>
          <div class="col-md-3 form-group">
            <label class="d-block">{{ $t('global.active') }}</label>
            <input v-model="form.is_active" type="checkbox" />
          </div>
        </div>

        <h6 class="mt-2">{{ $t('admin.features') }}</h6>
        <div class="row">
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.max_storage') }}</label>
            <input v-model.number="form.maxStorageMB" type="number" class="form-control" min="-1" step="1" />
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.max_canva_imports') }}</label>
            <input v-model.number="form.features.max_canva_imports" type="number" class="form-control" />
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.max_posts') }}</label>
            <input v-model.number="form.features.max_posts" type="number" class="form-control" />
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.max_accounts') }}</label>
            <input v-model.number="form.features.max_accounts" type="number" class="form-control" />
          </div>
          <div class="col-md-6 form-group">
            <label>{{ $t('admin.max_ai_tokens') }}</label>
            <input v-model.number="form.features.max_ai_tokens" type="number" class="form-control" min="-1" />
          </div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.api_enabled" type="checkbox" /> API</label></div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.mcp_enabled" type="checkbox" /> MCP</label></div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.ai_enabled" type="checkbox" /> AI</label></div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.recurring" type="checkbox" /> Recurring</label></div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.analytics" type="checkbox" /> Analytics</label></div>
          <div class="col-md-4 form-group"><label><input v-model="form.features.design_studio" type="checkbox" /> Design Studio</label></div>
        </div>

        <div class="text-right mt-3">
          <button class="btn btn-light btn-sm mr-2" @click="$modal.hide('plan-modal')">{{ $t('common.cancel') }}</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="savePlan">{{ $t('common.save') }}</button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

const MB = 1024 * 1024

function bytesToMB(bytes) {
  if (bytes === -1) return -1
  return Math.round((Number(bytes) || 0) / MB)
}

function mbToBytes(mb) {
  if (mb === -1) return -1
  return Math.round(Number(mb) || 0) * MB
}

const defaultFeatures = () => ({
  max_storage_bytes: 104857600,
  max_canva_imports: 1,
  api_enabled: true,
  mcp_enabled: true,
  max_posts: 10,
  max_accounts: 1,
  ai_enabled: false,
  max_ai_tokens: 0,
  recurring: false,
  analytics: false,
  design_studio: true,
})

export default {
  mixins: [i18nPage],
  data() {
    return { form: null, saving: false }
  },
  computed: {
    ...mapGetters('admin', ['plans', 'loading']),
    intervalOptions() {
      return [
        { value: 'month', label: this.$t('admin.interval_month') },
        { value: 'year', label: this.$t('admin.interval_year') },
      ]
    },
  },
  async created() {
    await this.fetchPlans()
  },
  methods: {
    ...mapActions('admin', ['fetchPlans', 'createPlan', 'updatePlan', 'deactivatePlan']),
    formatUSD(v) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0)
    },
    formatIDR(v) {
      if (!v) return '-'
      return new Intl.NumberFormat(this.intlLocale, { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(v)
    },
    openCreate() {
      this.form = {
        slug: '',
        name: '',
        description: '',
        price: 0,
        price_idr: 0,
        currency: 'USD',
        interval: 'month',
        paypal_plan_id: '',
        is_active: true,
        sort_order: 0,
        maxStorageMB: bytesToMB(defaultFeatures().max_storage_bytes),
        features: defaultFeatures(),
      }
      this.$modal.show('plan-modal')
    },
    openEdit(plan) {
      const features = { ...defaultFeatures(), ...(plan.features || {}) }
      this.form = {
        id: plan.id,
        slug: plan.slug,
        name: plan.name,
        description: plan.description,
        price: plan.price,
        price_idr: plan.price_idr,
        currency: plan.currency,
        interval: plan.interval,
        paypal_plan_id: plan.paypal_plan_id,
        is_active: plan.is_active,
        sort_order: plan.sort_order,
        maxStorageMB: bytesToMB(features.max_storage_bytes),
        features,
      }
      this.$modal.show('plan-modal')
    },
    async savePlan() {
      this.saving = true
      try {
        const payload = { ...this.form }
        delete payload.id
        delete payload.maxStorageMB
        payload.features = {
          ...payload.features,
          max_storage_bytes: mbToBytes(this.form.maxStorageMB),
        }
        if (this.form.id) {
          await this.updatePlan({ id: this.form.id, payload })
        } else {
          await this.createPlan(payload)
        }
        this.$toast?.success?.(this.$t('admin.plan_saved'))
        this.$modal.hide('plan-modal')
        await this.fetchPlans()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('common.save_failed'))
      } finally {
        this.saving = false
      }
    },
    async onDeactivate(plan) {
      const ok = await this.$confirm(this.$t('admin.deactivate_confirm', { name: plan.name }), { danger: true })
      if (!ok) return
      try {
        await this.deactivatePlan(plan.id)
        this.$toast?.success?.(this.$t('admin.plan_deactivated'))
        await this.fetchPlans()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('admin.deactivate_failed'))
      }
    },
  },
}
</script>

<style scoped>
.modal-pad { padding: 1.25rem; max-height: 80vh; overflow-y: auto; }
</style>
